import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Vote, 
  Plus, 
  MapPin,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  BarChart3,
  Trash2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Seggi = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [seggi, setSeggi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [selectedSeggio, setSelectedSeggio] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [form, setForm] = useState({
    nome: '',
    indirizzo: '',
    descrizione: '',
    data_elezione: '',
    orario_apertura: '08:00',
    orario_chiusura: '20:00',
    opzioni_voto: ''
  });

  const canGestisci = () => isSindacatoAdmin() || hasPermesso('gestisci_seggi');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/municipio/seggi`);
      setSeggi(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const opzioni = form.opzioni_voto.split('\n').filter(o => o.trim());
      await axios.post(`${API_URL}/municipio/seggi`, {
        ...form,
        opzioni_voto: opzioni
      });
      toast.success('Seggio creato');
      setDialogOpen(false);
      setForm({
        nome: '',
        indirizzo: '',
        descrizione: '',
        data_elezione: '',
        orario_apertura: '08:00',
        orario_chiusura: '20:00',
        opzioni_voto: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleChangeStato = async (id, stato) => {
    try {
      await axios.patch(`${API_URL}/municipio/seggi/${id}?stato=${stato}`);
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo seggio?')) return;
    try {
      await axios.delete(`${API_URL}/municipio/seggi/${id}`);
      toast.success('Seggio eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openVoteDialog = (seggio) => {
    setSelectedSeggio(seggio);
    setSelectedOptions([]);
    setVoteDialogOpen(true);
  };

  const handleVote = async () => {
    if (selectedOptions.length === 0) {
      toast.error('Seleziona almeno un\'opzione');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/municipio/seggi/${selectedSeggio.id}/vota`, selectedOptions);
      toast.success('Voto registrato!');
      setVoteDialogOpen(false);
      setSelectedSeggio(null);
      setSelectedOptions([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const toggleOption = (option) => {
    setSelectedOptions(prev => 
      prev.includes(option) 
        ? prev.filter(o => o !== option)
        : [...prev, option]
    );
  };

  const hasVoted = (seggio) => {
    return seggio.votanti?.includes(user?.id);
  };

  const getStatoBadge = (stato) => {
    const config = {
      PROGRAMMATO: { color: 'bg-slate-100 text-slate-800', icon: Clock },
      APERTO: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      CHIUSO: { color: 'bg-red-100 text-red-800', icon: XCircle },
      ANNULLATO: { color: 'bg-slate-200 text-slate-600', icon: XCircle }
    };
    const c = config[stato] || config.PROGRAMMATO;
    const Icon = c.icon;
    return (
      <Badge className={c.color}>
        <Icon className="w-3 h-3 mr-1" />
        {stato}
      </Badge>
    );
  };

  const getTotalVoti = (seggio) => {
    return Object.values(seggio.voti || {}).reduce((a, b) => a + b, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seggi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center">
            <Vote className="w-7 h-7 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Seggi Elettorali</h1>
            <p className="text-slate-500">Vota alle elezioni aperte</p>
          </div>
        </div>
        {canGestisci() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-500 hover:bg-indigo-600">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Seggio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Crea Seggio Elettorale</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Elezione</Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm({...form, nome: e.target.value})}
                    placeholder="es. Elezioni Sindaco 2026"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Indirizzo Seggio</Label>
                  <Input
                    value={form.indirizzo}
                    onChange={(e) => setForm({...form, indirizzo: e.target.value})}
                    placeholder="es. Via Roma 1, Municipio"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Textarea
                    value={form.descrizione}
                    onChange={(e) => setForm({...form, descrizione: e.target.value})}
                    placeholder="Descrizione dell'elezione..."
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Input
                      type="date"
                      value={form.data_elezione}
                      onChange={(e) => setForm({...form, data_elezione: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Apertura</Label>
                    <Input
                      type="time"
                      value={form.orario_apertura}
                      onChange={(e) => setForm({...form, orario_apertura: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Chiusura</Label>
                    <Input
                      type="time"
                      value={form.orario_chiusura}
                      onChange={(e) => setForm({...form, orario_chiusura: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Opzioni di Voto (una per riga)</Label>
                  <Textarea
                    value={form.opzioni_voto}
                    onChange={(e) => setForm({...form, opzioni_voto: e.target.value})}
                    placeholder={"Mario Rossi\nLuigi Verdi\nAnna Bianchi"}
                    rows={4}
                    required
                  />
                  <p className="text-xs text-slate-500">Inserisci ogni candidato/opzione su una riga separata</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600">Crea Seggio</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Lista Seggi */}
      {seggi.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Vote className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessun seggio elettorale</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {seggi.map((seggio) => (
            <Card key={seggio.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{seggio.nome}</CardTitle>
                    <CardDescription className="mt-1">{seggio.descrizione}</CardDescription>
                  </div>
                  {getStatoBadge(seggio.stato)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {seggio.indirizzo}
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {new Date(seggio.data_elezione).toLocaleDateString('it-IT')}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-slate-400" />
                    {seggio.orario_apertura} - {seggio.orario_chiusura}
                  </div>
                </div>
                
                {/* Opzioni e Risultati */}
                {seggio.opzioni_voto?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-700">
                        {seggio.stato === 'CHIUSO' ? 'Risultati' : 'Candidati/Opzioni'}
                      </p>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Users className="w-4 h-4" />
                        {seggio.votanti?.length || 0} votanti
                      </div>
                    </div>
                    <div className="space-y-2">
                      {seggio.opzioni_voto.map((opz, idx) => {
                        const voti = seggio.voti?.[opz] || 0;
                        const totale = getTotalVoti(seggio);
                        const percentuale = totale > 0 ? Math.round((voti / totale) * 100) : 0;
                        
                        return (
                          <div key={idx} className="relative">
                            <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg relative z-10">
                              <span className="text-sm font-medium">{opz}</span>
                              {(seggio.stato === 'CHIUSO' || canGestisci()) && (
                                <span className="text-sm text-slate-600">
                                  {voti} voti ({percentuale}%)
                                </span>
                              )}
                            </div>
                            {(seggio.stato === 'CHIUSO' || canGestisci()) && (
                              <div 
                                className="absolute inset-0 bg-indigo-100 rounded-lg transition-all"
                                style={{ width: `${percentuale}%` }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex gap-2 flex-wrap">
                {/* Pulsante Vota */}
                {seggio.stato === 'APERTO' && !hasVoted(seggio) && (
                  <Button 
                    onClick={() => openVoteDialog(seggio)}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    <Vote className="w-4 h-4 mr-2" />
                    Vota
                  </Button>
                )}
                {seggio.stato === 'APERTO' && hasVoted(seggio) && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Hai già votato
                  </Badge>
                )}
                
                {/* Controlli Admin */}
                {canGestisci() && (
                  <>
                    {seggio.stato === 'PROGRAMMATO' && (
                      <>
                        <Button 
                          size="sm" 
                          onClick={() => handleChangeStato(seggio.id, 'APERTO')}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          Apri Votazione
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleChangeStato(seggio.id, 'ANNULLATO')}
                        >
                          Annulla
                        </Button>
                      </>
                    )}
                    {seggio.stato === 'APERTO' && (
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleChangeStato(seggio.id, 'CHIUSO')}
                      >
                        Chiudi Votazione
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(seggio.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Voto */}
      <Dialog open={voteDialogOpen} onOpenChange={setVoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esprimi il tuo Voto</DialogTitle>
          </DialogHeader>
          {selectedSeggio && (
            <div className="space-y-4">
              <div className="bg-indigo-50 p-3 rounded-lg">
                <p className="font-medium text-indigo-900">{selectedSeggio.nome}</p>
                <p className="text-sm text-indigo-600">{selectedSeggio.descrizione}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Seleziona una o più opzioni (scelta multipla)</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedSeggio.opzioni_voto?.map((opz, idx) => (
                    <div 
                      key={idx}
                      onClick={() => toggleOption(opz)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedOptions.includes(opz) 
                          ? 'bg-indigo-50 border-indigo-300' 
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedOptions.includes(opz)}
                        onCheckedChange={() => toggleOption(opz)}
                      />
                      <span className="font-medium">{opz}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Hai selezionato {selectedOptions.length} opzion{selectedOptions.length === 1 ? 'e' : 'i'}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setVoteDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleVote}
                  disabled={selectedOptions.length === 0}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Vote className="w-4 h-4 mr-2" />
                  Conferma Voto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Seggi;
